import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

/**
 * House hourglass — the gem-filled glasses that keep score in the Great Hall.
 *
 * Each house has its own gemstone and its own metal on the frame:
 *   Gryffindor rubies / gold · Ravenclaw sapphires / bronze
 *   Slytherin emeralds / silver · Hufflepuff topaz / black
 *
 * Gems pour in on mount rather than starting settled.
 */

export type HouseKey = "gryffindor" | "ravenclaw" | "slytherin" | "hufflepuff";

const HOUSE: Record<HouseKey, {
  name: string; crest: string; gem: string; gemLit: string; gemDeep: string; metal: string; ink: string;
}> = {
  gryffindor: {
    name: "Gryffindor", crest: "\u{1F981}",
    gem: "#c1272d", gemLit: "#e8434a", gemDeep: "#6d0b10", metal: "#d3a625", ink: "#e8555c",
  },
  ravenclaw: {
    name: "Ravenclaw", crest: "\u{1F985}",
    gem: "#2c4a94", gemLit: "#5d80c8", gemDeep: "#141d3d", metal: "#946b2d", ink: "#7098dd",
  },
  slytherin: {
    name: "Slytherin", crest: "\u{1F40D}",
    gem: "#1a7a45", gemLit: "#2fbd6d", gemDeep: "#0c2f1c", metal: "#c0c0c0", ink: "#43c47e",
  },
  hufflepuff: {
    name: "Hufflepuff", crest: "\u{1F9A1}",
    gem: "#e0a81f", gemLit: "#f7cf58", gemDeep: "#6f5008", metal: "#372e29", ink: "#f0c249",
  },
};

interface Props {
  house: HouseKey;
  /** 0–100, how full the lower chamber settles */
  fill: number;
  value: React.ReactNode;
  label: string;
  sub?: string;
  delay?: number;
}

export const HouseHourglass: React.FC<Props> = ({ house, fill, value, label, sub, delay = 0 }) => {
  const h = HOUSE[house];
  const [poured, setPoured] = useState(false);
  const settled = Math.max(0, Math.min(100, fill));

  useEffect(() => {
    const id = window.setTimeout(() => setPoured(true), 260 + delay * 1000);
    return () => window.clearTimeout(id);
  }, [delay]);

  return (
    <Column title={`${h.name} — ${label}`}>
      <Crest>{h.crest}</Crest>

      <Glass $gem={h.gemLit}>
        <Cap $metal={h.metal} />
        <Body>
          <Chamber $top>
            <SandTop $fill={poured ? 100 - settled : 100} $gem={h.gemLit} $deep={h.gem} />
          </Chamber>
          <Neck />
          <Chamber>
            <SandBottom $fill={poured ? settled : 0} $gem={h.gemLit} $deep={h.gemDeep} />
            <Stream $gem={h.gemLit} />
          </Chamber>
        </Body>
        <Cap $metal={h.metal} />
      </Glass>

      <Value $ink={h.ink}>{value}</Value>
      <Label>{label}</Label>
      {sub && <Sub>{sub}</Sub>}
    </Column>
  );
};

export default HouseHourglass;

/* ==================== styles ==================== */

const gemGlint = keyframes`
  0%, 100% { opacity: 0.5; }
  50%      { opacity: 0.95; }
`;

const streamFall = keyframes`
  0%   { transform: translateY(-16px) scaleY(0.4); opacity: 0; }
  25%  { opacity: 0.9; }
  100% { transform: translateY(16px) scaleY(1); opacity: 0; }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
`;

const Crest = styled.div`
  font-size: 0.95rem;
  margin-bottom: 0.3rem;
  filter: grayscale(0.25);
`;

const Glass = styled.div<{ $gem: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.35s cubic-bezier(0.34, 1.4, 0.64, 1), filter 0.35s ease;

  ${Column}:hover & {
    transform: translateY(-3px) scale(1.06);
    filter: drop-shadow(0 0 12px ${({ $gem }) => $gem}55);
  }
`;

const Cap = styled.div<{ $metal: string }>`
  width: 40px;
  height: 5px;
  border-radius: 2px;
  background: ${({ $metal }) => `linear-gradient(180deg, ${$metal}, ${$metal}99)`};
  box-shadow: 0 0 8px ${({ $metal }) => $metal}55;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2px 0;
`;

const Chamber = styled.div<{ $top?: boolean }>`
  position: relative;
  width: 32px;
  height: 25px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.045);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  clip-path: ${({ $top }) => $top
    ? "polygon(0% 0%, 100% 0%, 58% 100%, 42% 100%)"
    : "polygon(42% 0%, 58% 0%, 100% 100%, 0% 100%)"};
`;

const Neck = styled.div`
  width: 5px;
  height: 3px;
  background: rgba(255, 255, 255, 0.09);
`;

const SandTop = styled.div<{ $fill: number; $gem: string; $deep: string }>`
  position: absolute;
  top: 0; left: 0; right: 0;
  height: ${({ $fill }) => Math.max($fill, 8)}%;
  background: linear-gradient(180deg, ${({ $gem }) => $gem}, ${({ $deep }) => $deep});
  opacity: 0.85;
  transition: height 1.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SandBottom = styled.div<{ $fill: number; $gem: string; $deep: string }>`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: ${({ $fill }) => $fill}%;
  background: linear-gradient(180deg, ${({ $gem }) => $gem}, ${({ $deep }) => $deep});
  box-shadow: inset 0 2px 6px rgba(255, 255, 255, 0.22);
  transition: height 1.6s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${gemGlint} 3.6s ease-in-out infinite;
`;

const Stream = styled.div<{ $gem: string }>`
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 16px;
  margin-left: -1px;
  border-radius: 1px;
  background: ${({ $gem }) => $gem};
  animation: ${streamFall} 1.5s linear infinite;
`;

const Value = styled.div<{ $ink: string }>`
  margin-top: 0.5rem;
  font-family: "Cinzel", serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ $ink }) => $ink};
  text-shadow: 0 0 14px ${({ $ink }) => $ink}45;
  line-height: 1;
`;

const Label = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.48rem;
  color: #7a6a8a;
  margin-top: 0.22rem;
  text-align: center;
`;

const Sub = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.42rem;
  color: #5a4a6a;
  margin-top: 0.1rem;
  text-align: center;
`;
